import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PLACEHOLDER_IMG, safeUrl, sized } from "@/lib/media";

const HOVER_MS = 1800;      // while the mouse is on the card
const IN_VIEW_MS = 3200;    // on screen (phones have no hover, so this keeps them moving too)
const MAX_IMAGES = 8;

/**
 * Property card picture that slides through the property's photos.
 * It moves on by itself while the card is on screen, faster while hovered, and shows small arrows on hover.
 * On touch screens the arrows are always visible and the photo can be swiped. Photos load only when needed.
 */
export default function CardImageSlider({ property, to, alt, active }) {
    const images = useMemo(() => {
        const list = [property.cover_image, ...(property.images || [])].map(safeUrl).filter(Boolean);
        const unique = [...new Set(list)].slice(0, MAX_IMAGES);
        return unique.length ? unique : [PLACEHOLDER_IMG];
    }, [property.cover_image, property.images]);
    const [idx, setIdx] = useState(0);
    const [loadedUpTo, setLoadedUpTo] = useState(0);
    const [inView, setInView] = useState(false);
    const root = useRef(null);
    const touchX = useRef(null);
    const jitter = useRef(Math.round(Math.random() * 1200));   // so cards side by side don't all flip together
    const many = images.length > 1;

    const go = (next) => {
        const n = (next + images.length) % images.length;
        setIdx(n);
        setLoadedUpTo((m) => Math.max(m, Math.min(n + 1, images.length - 1)));
    };

    useEffect(() => {
        if (!many || !root.current || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.6 });
        io.observe(root.current);
        return () => io.disconnect();
    }, [many]);

    useEffect(() => {
        if (!many || !(active || inView)) return undefined;
        setLoadedUpTo((m) => Math.max(m, 1));                   // fetch the next photo before it is shown
        const wait = active ? HOVER_MS : IN_VIEW_MS + jitter.current;
        const t = setTimeout(() => go(idx + 1), wait);           // restarts after every change, including arrow clicks
        return () => clearTimeout(t);
    }, [many, active, inView, idx]); // eslint-disable-line react-hooks/exhaustive-deps

    const arrow = (dir) => (e) => { e.preventDefault(); e.stopPropagation(); go(idx + dir); };

    return (
        <div ref={root} className="absolute inset-0"
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
                if (touchX.current == null || !many) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                touchX.current = null;
                if (Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
            }}>
            <Link to={to} className="block h-full overflow-hidden" aria-label={`View ${property.name}`}>
                <div className="flex h-full transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * 100}%)` }}>
                    {images.map((src, i) => (
                        <img key={src + i}
                            src={i <= loadedUpTo ? sized(src, 800) : undefined}
                            alt={i === 0 ? alt : ""}
                            aria-hidden={i === 0 ? undefined : "true"}
                            className="w-full h-full object-cover shrink-0 bg-slate-100"
                            loading={i === 0 ? "lazy" : "eager"} decoding="async"
                            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }} />
                    ))}
                </div>
            </Link>
            {many && (
                <>
                    <button type="button" onClick={arrow(-1)} aria-label="Previous photo"
                        className="card-slide-arrow left-2.5"><ChevronLeft size={17} aria-hidden="true" /></button>
                    <button type="button" onClick={arrow(1)} aria-label="Next photo"
                        className="card-slide-arrow right-2.5"><ChevronRight size={17} aria-hidden="true" /></button>
                    <div className="absolute bottom-1.5 inset-x-0 flex justify-center gap-1 pointer-events-none z-10" aria-hidden="true">
                        {images.map((_, i) => (
                            <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === idx ? "w-4 bg-white" : "w-1 bg-white/60"}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
